import { usePostHog } from '@posthog/react';
import type { PostHog } from 'posthog-js';
import { useStore } from 'react-redux';
import useAuth from 'hooks/useAuth';
import { useDispatch } from 'store';
import { APIUserSlice, useGetUserDataQuery, useSaveCurrentUserPreferencesQuietlyMutation } from 'store/api/user';
import { hasNewDesign, type UiDesign, type UpdateUserPreferences } from 'types/user';
import { infoAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { designInputsOf, isImpersonating, keepImpersonationDesign, useIsNewDesignOf } from 'utils/impersonationDesign';

// The last preferences write queued. The backend replaces the document wholesale, so writes leave one at a time,
// in the order they were made: the Welcome saves seen as it shows, right behind the switch that showed it, and two
// writes crossing on the way would let the older document land last and drop the newer key.
let lastWrite: Promise<unknown> = Promise.resolve();

const selectCurrentUser = APIUserSlice.endpoints.getUserData.select();

/** Where a switch was flipped, as `new_design_toggled` reports it. */
export type DesignToggleSource = 'header' | 'sidebar' | 'rail' | 'preferences';

/** What every switch ends with: the event, and on the way back to classic the toast naming the way in. */
export function announceDesignSwitch(posthog: PostHog, design: UiDesign, source: DesignToggleSource) {
    posthog.capture('new_design_toggled', { enabled: design === 'NEW', source });
    if (design === 'CLASSIC') {
        infoAlert(t('design.switched-to-classic'));
    }
}

/**
 * The effective design. It reads the current-user query, not `useAuth().user`, which is the sign-in
 * snapshot and would miss a switch. While signed out the query is skipped: a 401 from it logs out. The tenant's
 * new-design flag rides the same payload, so `isLoading` covers it too. While impersonating, the design is the
 * one chosen in this browser when there is one, and nothing here saves: the preferences are the other user's.
 */
export function useUiDesign() {
    const { user } = useAuth();
    const { data, isLoading } = useGetUserDataQuery(undefined, { skip: !user });
    const [updatePreferences] = useSaveCurrentUserPreferencesQuietlyMutation();
    const dispatch = useDispatch();
    const store = useStore<Parameters<typeof selectCurrentUser>[0]>();
    const posthog = usePostHog();

    const preferences = data?.preferences;
    const offered = hasNewDesign(data);
    const impersonating = isImpersonating(data);
    const design: UiDesign = useIsNewDesignOf(designInputsOf(data)) ? 'NEW' : 'CLASSIC';

    // The backend replaces the document wholesale, so the whole of it goes with the changed keys: the cached
    // document as the write leaves, not the one the calling render saw, which misses every change made since. The
    // cached user takes the change first, so the layout follows the click rather than the round trip. A failed
    // write takes back its own change, before the next write reads the cache. The failure is toasted by the API's
    // error handler, which stays silent in tour mode. Nothing is patched or sent while impersonating, as the
    // call is made and again as the write leaves: the session may have changed hands while it waited.
    const savePreferences = (change: Partial<UpdateUserPreferences>): Promise<boolean> => {
        const current = selectCurrentUser(store.getState()).data;
        if (!current || isImpersonating(current)) return Promise.resolve(false);
        const patch = dispatch(
            APIUserSlice.util.updateQueryData('getUserData', undefined, (draft) => {
                Object.assign(draft.preferences, change);
            })
        );
        const write = lastWrite.then(async () => {
            const latest = selectCurrentUser(store.getState()).data;
            // Signed out since, or acting as someone else: the user the change was for is no longer the session's.
            if (!latest || isImpersonating(latest)) return false;
            try {
                await updatePreferences({ ...latest.preferences, ...change }).unwrap();
                return true;
            } catch {
                patch.undo();
                return false;
            }
        });
        lastWrite = write;
        return write;
    };

    const setDesign = async (next: UiDesign, source: DesignToggleSource) => {
        if (data && impersonating) {
            keepImpersonationDesign(data, next);
        } else if (!(await savePreferences({ uiDesign: next }))) {
            return;
        }
        announceDesignSwitch(posthog, next, source);
    };

    return {
        design,
        isNew: design === 'NEW',
        /** True until the current user has been read once. */
        isLoading,
        /** False without the tenant's flag. While impersonating the switch works, and saves nothing. */
        canToggle: offered && !!data,
        /** True while the session acts as someone else: a control that saves the design does not show then. */
        isImpersonating: impersonating,
        // Seen while impersonating, so the Welcome never opens by itself: it saves seen as it does.
        welcomeSeen: impersonating || preferences?.newDesignWelcomeSeen === true,
        setDesign,
        markWelcomeSeen: () => savePreferences({ newDesignWelcomeSeen: true }),
    };
}
